import { isNonEmptyArray } from '../../../utils';
import { filterExistsAndExcludes } from '../helpers';

/**
 * Rule definitions for client-side filtering logic.
 *
 * These rules are applied to data rows to determine if they should be included or excluded
 * based on the current filter state. Multiple rules can be combined - all rules must pass
 * for a row to be included.
 *
 * @template TFilter - The type of the filter object
 *
 * @example
 * ```ts
 * // Include only rows where the data value is in the filter array (whitelist behavior)
 * { type: 'include', filterKey: 'Symbols', dataKey: 'Symbol' }
 *
 * // Match if any value in data array is in filter array
 * { type: 'arrayMatch', filterKey: 'Markets', dataKey: 'Markets' }
 *
 * // Match if base or quote currency is in filter
 * { type: 'currency', filterKey: 'Currencies', baseKey: 'BaseCurrency', quoteKey: 'QuoteCurrency' }
 *
 * // Custom predicate function for arbitrary filtering logic
 * { type: 'custom', filterKey: 'CustomField', predicate: (filterValue, data) => boolean }
 * ```
 */
export type ClientSideFilterRule<TFilter extends Record<string, any>> =
  | {
      type: 'include';
      filterKey: keyof TFilter;
      dataKey: string;
    }
  | {
      type: 'arrayMatch';
      filterKey: keyof TFilter;
      dataKey: string;
    }
  | {
      type: 'currency';
      filterKey: keyof TFilter;
      baseKey: string;
      quoteKey: string;
    }
  | {
      type: 'custom';
      filterKey: keyof TFilter;
      predicate: (filterValue: any, data: any) => boolean;
    };

/**
 * Creates a client-side filter function based on the provided rules.
 *
 * The returned function evaluates each data row against all rules sequentially.
 * If any rule fails (excludes the row), the function returns false.
 * Only if all rules pass does the function return true.
 *
 * @template TFilter - The type of the filter object
 * @param config - Configuration object containing the filter and rules
 * @param config.filter - The current filter state
 * @param config.rules - Array of filter rules to apply
 * @returns A function that takes a row object and returns whether it should be included
 *
 * @example
 * ```ts
 * const filterFn = createClientSideFilter({
 *   filter: { Symbols: ['BTC-USD', 'ETH-USD'] },
 *   rules: [
 *     { type: 'include', filterKey: 'Symbols', dataKey: 'Symbol' }
 *   ]
 * });
 *
 * filterFn({ data: { Symbol: 'BTC-USD' } }); // true
 * filterFn({ data: { Symbol: 'LTC-USD' } }); // false
 * ```
 */
export function createClientSideFilter<TFilter extends Record<string, any>>(config: {
  filter: TFilter;
  rules: ClientSideFilterRule<TFilter>[];
}) {
  return (row: { data: any | null | undefined }): boolean => {
    const { filter, rules } = config;
    const data = row.data;

    if (!data) {
      return false;
    }

    // Add rules as we extend usage of this helper
    for (const rule of rules) {
      if (rule.type === 'include') {
        if (filterExistsAndExcludes(filter, rule.filterKey, data, rule.dataKey)) {
          return false;
        }
      } else if (rule.type === 'arrayMatch') {
        const filterValue = filter[rule.filterKey];
        const dataValue = data[rule.dataKey];
        if (isNonEmptyArray(filterValue) && Array.isArray(dataValue)) {
          if (!dataValue.some((item: any) => filterValue.includes(item))) {
            return false;
          }
        }
      } else if (rule.type === 'currency') {
        const filterValue = filter[rule.filterKey];
        if (isNonEmptyArray(filterValue)) {
          if (!filterValue.includes(data[rule.baseKey]) && !filterValue.includes(data[rule.quoteKey])) {
            return false;
          }
        }
      } else if (rule.type === 'custom') {
        const filterValue = filter[rule.filterKey];
        if (!rule.predicate(filterValue, data)) {
          return false;
        }
      }
    }

    return true;
  };
}
